import { type FC, useEffect, useState } from 'react';
import type {
  Control,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch
} from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import {
  RadioButton as Radio,
  Text,
  TextField
} from '@cencosud-cencommerce/paris-uikit';

import {
  type IPickupIdForm,
  PICKUP_FULL_NAME_MAX_LENGTH
} from '@/Domain/PickupId';
import { IdentificationDocumentTypes } from '@/modules/shared/types/IdentificationDocumentType';

interface Props {
  register?: UseFormRegister<IPickupIdForm>;
  control: Control<IPickupIdForm>;
  watch?: UseFormWatch<IPickupIdForm>;
  setValue?: UseFormSetValue<IPickupIdForm>;
  clearErrors?: UseFormClearErrors<IPickupIdForm>;
  'data-testid'?: string;
  trigger: UseFormTrigger<IPickupIdForm>;
  isDniEnabled: boolean;
}

const LABEL_DOCUMENT_TYPE = '¿Cuál es tu número de documento?';
const LABEL_DOCUMENT_TYPE_ID = 'RUT (Chile)';
const LABEL_DOCUMENT_TYPE_DNI = 'DNI (otros países)';
const LABEL_DOCUMENT_NUMBER_RUT = 'RUT (*)';
const LABEL_DOCUMENT_NUMBER_FOREIGN = 'Número de documento (*)';

const PickupIdForm: FC<Props> = ({
  control,
  'data-testid': dataTestId = 'paris-pickupidform',
  setValue,
  clearErrors,
  trigger,
  isDniEnabled
}) => {
  const [isFullNameFocused, setIsFullNameFocused] = useState(false);
  const [isDocumentIDFocused, setIsDocumentIDFocused] = useState(false);

  // Watch documentType to change labels dynamically
  const documentType = useWatch({
    control,
    name: 'documentType',
    defaultValue: IdentificationDocumentTypes.RUT
  });
  const effectiveDocumentType = isDniEnabled
    ? documentType
    : IdentificationDocumentTypes.RUT;

  useEffect(() => {
    if (!isDniEnabled && setValue) {
      setValue('documentType', IdentificationDocumentTypes.RUT, {
        shouldDirty: false,
        shouldValidate: false
      });
    }
  }, [isDniEnabled, setValue]);

  return (
    <div
      className="co-flex co-flex-col co-gap-6 co-mt-4"
      data-testid={dataTestId}
    >
      <Text className="co-text-right" variant="caption">
        * Campos obligatorios
      </Text>

      {/* Campo: Nombre Completo */}
      <Controller
        control={control}
        name="fullname"
        render={({ field, fieldState, formState }) => (
          <TextField
            {...field}
            label="Nombre y apellido (*)"
            helperText={
              isFullNameFocused
                ? 'Nombre completo de la persona que hará el retiro.'
                : fieldState.error?.message || ''
            }
            maxLength={PICKUP_FULL_NAME_MAX_LENGTH}
            status={
              (formState.isSubmitted || fieldState.isTouched) &&
              fieldState.invalid
                ? 'error'
                : fieldState.isTouched && !isFullNameFocused
                  ? 'validated'
                  : undefined
            }
            onFocus={() => setIsFullNameFocused(true)}
            onBlur={async () => {
              field.onBlur();
              await trigger('fullname');
              setIsFullNameFocused(false);
            }}
          />
        )}
      />

      {/* Campo: ¿Número de documento? */}
      {isDniEnabled ? (
        <Controller
          name="documentType"
          control={control}
          defaultValue="RUT"
          render={({ field: { value, onChange, ...rest } }) => (
            <div>
              <label
                className="co-block co-mb-6 co-font-medium"
                style={{
                  fontSize: '14px'
                }}
              >
                <span
                  // * UI Kit font size workaround. Couldn't have 16px directly on desktop
                  style={{
                    fontSize: window.innerWidth >= 1024 ? '16px' : '14px'
                  }}
                >
                  {LABEL_DOCUMENT_TYPE}
                </span>
              </label>
              <div className="co-flex co-flex-col co-gap-4">
                <Radio
                  {...rest}
                  label={LABEL_DOCUMENT_TYPE_ID}
                  checked={
                    effectiveDocumentType === IdentificationDocumentTypes.RUT
                  }
                  onChange={() => {
                    onChange(IdentificationDocumentTypes.RUT);
                    setValue?.('documentID', '', {
                      shouldValidate: false,
                      shouldDirty: false
                    });
                    clearErrors?.('documentID');
                  }}
                  variant="card"
                />
                <Radio
                  {...rest}
                  label={LABEL_DOCUMENT_TYPE_DNI}
                  checked={value === IdentificationDocumentTypes.DNI}
                  onChange={() => {
                    onChange(IdentificationDocumentTypes.DNI);
                    setValue?.('documentID', '', {
                      shouldValidate: false,
                      shouldDirty: false
                    });
                    clearErrors?.('documentID');
                  }}
                  variant="card"
                />
              </div>
            </div>
          )}
        />
      ) : null}

      {/* Campo: Número de Documento (dinámico según documentType) */}
      <Controller
        control={control}
        name="documentID"
        render={({ field, fieldState, formState }) => (
          <TextField
            {...field}
            label={
              effectiveDocumentType === IdentificationDocumentTypes.RUT
                ? LABEL_DOCUMENT_NUMBER_RUT
                : LABEL_DOCUMENT_NUMBER_FOREIGN
            }
            type={
              effectiveDocumentType === IdentificationDocumentTypes.RUT
                ? 'rut'
                : 'text'
            }
            helperText={fieldState.error?.message && fieldState.error.message}
            status={
              (formState.isSubmitted || fieldState.isTouched) &&
              fieldState.invalid
                ? 'error'
                : fieldState.isTouched && !isDocumentIDFocused
                  ? 'validated'
                  : undefined
            }
            maxLength={18}
            onFocus={() => setIsDocumentIDFocused(true)}
            onBlur={async () => {
              field.onBlur();
              await trigger('documentID');
              setIsDocumentIDFocused(false);
            }}
          />
        )}
      />
    </div>
  );
};

export default PickupIdForm;
